import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Textarea } from '../../ui/textarea'
import { Label } from '../../ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'
import { Alert, AlertDescription } from '../../ui/alert'
import { Lock, Unlock, Upload, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react'
import { api } from '../../../lib/api/api'
import toast from 'react-hot-toast'

interface EncryptedImageViewerProps {
  documentId: string
  label: string
  mimeType?: string
}

export const EncryptedImageViewer: React.FC<EncryptedImageViewerProps> = ({
  documentId,
  label,
  mimeType = 'image/jpeg'
}) => {
  const [isDecrypted, setIsDecrypted] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isDecrypting, setIsDecrypting] = useState(false)
  const [privateKey, setPrivateKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Cleanup image URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [imageUrl])

  const handleDecrypt = async () => {
    setError(null)
    
    // Basic validation
    if (!privateKey || privateKey.trim() === '') {
      setError('Please provide a private key')
      return
    }
    
    // Validate PEM format
    if (!privateKey.includes('-----BEGIN') || !privateKey.includes('-----END')) {
      setError('Invalid PEM format. Key must include BEGIN and END markers.')
      return
    }
    
    setIsDecrypting(true)
    
    try {
      // Call backend decryption API
      const response = await api.post(
        `/api/v1/blockchain/documents/${documentId}/decrypt`,
        { privateKey },
        { responseType: 'blob' }
      )
      
      // Create object URL from blob response
      const blob = response.data
      const objectUrl = URL.createObjectURL(blob)
      
      setImageUrl(objectUrl)
      setIsDecrypted(true)
      setDialogOpen(false)
      toast.success(`${label} decrypted successfully`)
      
      // Clear private key from memory
      setPrivateKey('')
    } catch (err: any) {
      let errorMsg = 'Decryption failed'
      
      if (err.response) {
        const status = err.response.status
        
        // Handle 404 specifically - document may have been deleted
        if (status === 404) {
          errorMsg = 'Document not found - May have been deleted for compliance reasons'
        } else {
          // Try to parse error response for other statuses
          try {
            // Check if response is JSON (blob response)
            if (err.response.data instanceof Blob) {
              const text = await err.response.data.text()
              const errorData = JSON.parse(text)
              errorMsg = errorData.error || errorMsg
            } else {
              // Direct JSON response
              errorMsg = err.response.data?.error || errorMsg
            }
          } catch {
            // Fallback messages for different status codes
            if (status === 400) {
              errorMsg = 'Invalid private key or decryption parameters'
            } else if (status >= 500) {
              errorMsg = 'Server error occurred during decryption'
            } else {
              errorMsg = `Server error: ${status}`
            }
          }
        }
      } else if (err.message) {
        errorMsg = err.message
      }
      
      setError(errorMsg)
      toast.error(`Failed to decrypt ${label}: ${errorMsg}`)
      console.error('Decryption error:', err)
    } finally {
      setIsDecrypting(false)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    try {
      const contents = await readPemFile(file)
      setPrivateKey(contents)
      setError(null)
      toast.success('Private key loaded from file')
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to read file'
      setError(errorMsg)
      toast.error(errorMsg)
    }
  }
  
  const readPemFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (event) => {
        const contents = event.target?.result as string
        if (!contents) {
          reject(new Error('Failed to read file contents'))
          return
        }
        resolve(contents)
      }
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }
      
      reader.readAsText(file)
    })
  }

  const handleClearKey = () => {
    setPrivateKey('')
    setError(null)
  }

  if (!documentId || documentId.trim() === '') {
    return (
      <div className="p-4 border border-dashed rounded-lg text-center text-muted-foreground">
        <p className="text-sm">No {label.toLowerCase()} available</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      
      {!isDecrypted ? (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Card className="cursor-pointer hover:bg-muted/50 transition-colors border-dashed">
              <CardContent className="p-6 flex flex-col items-center justify-center space-y-3">
                <Lock className="h-12 w-12 text-muted-foreground" />
                <p className="text-sm font-medium">Encrypted Image</p>
                <Button variant="outline" size="sm">
                  <Unlock className="h-4 w-4 mr-2" />
                  Decrypt to View
                </Button>
              </CardContent>
            </Card>
          </DialogTrigger>
          
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Decrypt {label}</DialogTitle>
              <DialogDescription>
                Provide your RSA private key to decrypt and view the image.
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="paste" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="paste">Paste Key</TabsTrigger>
                <TabsTrigger value="upload">Upload PEM File</TabsTrigger>
              </TabsList>
              
              <TabsContent value="paste" className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="private-key">Private Key (PEM format)</Label>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowKey(!showKey)}
                      >
                        {showKey ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            Hide
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            Show
                          </>
                        )}
                      </Button>
                      {privateKey && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearKey}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                  <Textarea
                    id="private-key"
                    placeholder="-----BEGIN PRIVATE KEY-----
...
-----END PRIVATE KEY-----"
                    value={privateKey}
                    onChange={(e) => setPrivateKey(e.target.value)}
                    className={`font-mono text-xs min-h-32 ${!showKey && privateKey ? 'text-security-disc' : ''}`}
                    style={!showKey && privateKey ? { WebkitTextSecurity: 'disc' } as any : {}}
                  />
                </div>
                
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    <strong>Security Notice:</strong> Your private key is never transmitted or stored.
                  </AlertDescription>
                </Alert>
                
                <Button
                  onClick={handleDecrypt}
                  disabled={!privateKey || isDecrypting}
                  className="w-full"
                >
                  {isDecrypting ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Unlock className="h-4 w-4 mr-2" />
                      Decrypt Image
                    </>
                  )}
                </Button>
              </TabsContent>
              
              <TabsContent value="upload" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pem-file">Upload Private Key File (.pem)</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="pem-file"
                      type="file"
                      accept=".pem,.key"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('pem-file')?.click()}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose PEM File
                    </Button>
                  </div>
                  {privateKey && (
                    <div className="p-2 bg-muted rounded-md">
                      <p className="text-xs text-muted-foreground">
                        ✓ Private key loaded ({privateKey.length} characters)
                      </p>
                    </div>
                  )}
                </div>
                
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    <strong>Security Notice:</strong> Decryption happens entirely in your browser. Your private key is never transmitted or stored.
                  </AlertDescription>
                </Alert>
                
                <Button
                  onClick={handleDecrypt}
                  disabled={!privateKey || isDecrypting}
                  className="w-full"
                >
                  {isDecrypting ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Unlock className="h-4 w-4 mr-2" />
                      Decrypt Image
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      ) : (
        <Card>
          <CardContent className="p-4">
            {imageUrl && (
              <img
                src={imageUrl}
                alt={label}
                className="w-full h-auto rounded-md"
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
